import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Loader2, RotateCcw, ShieldAlert, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchCatalog, submitCampaign } from '../../api/a2p.js';
import { useA2pBypass } from '../../lib/a2pBypassFlag.js';
import A2PBypassBanner from '../../components/A2PBypassBanner.jsx';

const STEPS = ['About', 'Use case', 'Messages', 'Call-to-action', 'Review'];

export default function CreateA2PCampaignWizard({ open, brand, onClose, onCreated }) {
  const { hideUI: hideA2pUI } = useA2pBypass();
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState(null);
  const [submitting, setSubmittingFlag] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [useCase, setUseCase] = useState('MIXED');
  const [useCaseDescription, setUseCaseDescription] = useState('');
  const [sample1, setSample1] = useState('');
  const [sample2, setSample2] = useState('');
  const [cta, setCta] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setServerError(null);
    setAgreed(false);
    fetchCatalog().then((r) => {
      if (!r?.ok) return;
      setCatalog(r.catalog);
      setUseCaseDescription(r.catalog.descriptions?.[0] || '');
      const s = r.catalog.sampleMessages || [];
      setSample1(s[0] || '');
      setSample2(s[1] || '');
      setCta(r.catalog.cta || '');
    }).catch((e) => toast.error(e.message || 'Could not load wizard catalog'));
  }, [open]);

  if (!open) return null;

  // Bypassed tenants use the parent CIVIX Brand — no campaign to create.
  if (hideA2pUI) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="a2p-wizard-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div onClick={(e) => e.stopPropagation()} className="panel max-w-2xl w-full p-6 my-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 id="a2p-wizard-title" className="font-display font-bold text-lg">Create A2P Campaign</h2>
            <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <A2PBypassBanner />
        </div>
      </div>
    );
  }

  function resetMessages() {
    if (!catalog) return;
    setSample1(catalog.sampleMessages?.[0] || '');
    setSample2(catalog.sampleMessages?.[1] || '');
  }
  function resetCta() {
    if (!catalog) return;
    setCta(catalog.cta || '');
  }

  function next() {
    setServerError(null);
    if (step === 1 && !useCaseDescription.trim()) {
      toast.error('Pick a use case description');
      return;
    }
    if (step === 2 && (!sample1.trim() || !sample2.trim())) {
      toast.error('Both sample messages are required');
      return;
    }
    if (step === 3 && !cta.trim()) {
      toast.error('CTA description is required');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }
  function back() {
    setServerError(null);
    setStep((s) => Math.max(0, s - 1));
  }

  async function submit() {
    if (!agreed) {
      toast.error('You must agree to the charges to submit');
      return;
    }
    setSubmittingFlag(true);
    setServerError(null);
    try {
      const r = await submitCampaign({
        useCase,
        useCaseDescription,
        sampleMessages: [sample1.trim(), sample2.trim()],
        ctaDescription: cta.trim(),
        agreedToTerms: true,
      });
      if (r?.ok) {
        toast.success('A2P campaign submitted for review');
        onCreated?.(r.campaign);
        onClose();
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Submission failed';
      const code = err.response?.data?.code;
      setServerError({ msg, code });
      if (code === 'card_declined') {
        toast.error('Card declined — please update your payment method.');
      } else if (code === 'no_active_subscription') {
        toast.error('You need an active CIVIX subscription before creating campaigns.');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmittingFlag(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="a2p-wizard-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="panel max-w-2xl w-full p-6 my-8 space-y-5">
        <div className="flex items-center justify-between">
          <h2 id="a2p-wizard-title" className="font-display font-bold text-lg">
            Create A2P Campaign
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {/* Progress dots */}
        <ol className="flex items-center gap-1.5 text-xs flex-wrap">
          {STEPS.map((label, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <React.Fragment key={label}>
                <li
                  aria-current={active ? 'step' : undefined}
                  className={`px-3 py-1.5 rounded-md border flex items-center gap-2 ${
                    active
                      ? 'bg-civix-purple/30 text-white border-civix-purple/60'
                      : done
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : 'text-white/40 border-civix-border/50'
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-black/30 inline-flex items-center justify-center text-[10px] font-mono">
                    {done ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  {label}
                </li>
                {i < STEPS.length - 1 && <span className="text-white/20">›</span>}
              </React.Fragment>
            );
          })}
        </ol>

        {/* STEP 1 — About */}
        {step === 0 && (
          <div className="space-y-3">
            <div className="text-sm text-white/80">
              This campaign will be created under your existing approved brand:{' '}
              <strong className="text-white">{brand?.legalBusinessName || '—'}</strong>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-xs">
              <Field label="Brand SID" value={brand?.brandSid} />
              <Field label="Customer Profile SID" value={brand?.customerProfileSid} />
              <Field label="Brand status" value={brand?.brandStatus} />
              <Field label="Trust score" value={String(brand?.trustScore ?? 0)} />
            </div>
            <div className="text-xs text-white/50">
              No new brand vetting fee — only Twilio's $15 TCR campaign activation + first-month $10 campaign fee
              ($25 total today, charged at cost).
            </div>
          </div>
        )}

        {/* STEP 2 — Use case */}
        {step === 1 && catalog && (
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="use-case">Select a use case for this A2P campaign</label>
              <select
                id="use-case"
                className="input mt-1"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
              >
                {catalog.useCases.map((u) => (
                  <option key={u.id} value={u.id}>{u.label}</option>
                ))}
              </select>
            </div>

            <div className="rounded-md border border-civix-purple/40 bg-civix-purple/10 p-3 text-xs text-white/80 space-y-1">
              <div><strong>Today (one-time): $25</strong> — $15 TCR campaign activation + first month's Twilio campaign fee</div>
              <div><strong>Starting month 2: $10/mo</strong> — Twilio's monthly campaign fee, billed at cost</div>
              <div className="italic text-white/55 pt-1">CIVIX charges these at cost — we don't mark up A2P fees.</div>
            </div>

            <div>
              <label className="label" htmlFor="use-case-desc">Select a use case description</label>
              <select
                id="use-case-desc"
                className="input mt-1"
                value={useCaseDescription}
                onChange={(e) => setUseCaseDescription(e.target.value)}
              >
                {catalog.descriptions.map((d, i) => (
                  <option key={i} value={d}>{d.slice(0, 90)}{d.length > 90 ? '…' : ''}</option>
                ))}
              </select>
              <div className="text-xs text-white/55 mt-2 whitespace-pre-wrap">{useCaseDescription}</div>
            </div>

            <div className="pt-3 border-t border-civix-border/40">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">T-Mobile message limits (by trust score)</div>
              <table className="w-full text-xs">
                <thead className="text-white/50">
                  <tr><th className="text-left">Score</th><th className="text-right">Daily messages</th></tr>
                </thead>
                <tbody>
                  {catalog.tMobileLimits.map((row) => (
                    <tr key={row.range}>
                      <td className="py-1">{row.range}</td>
                      <td className="text-right font-mono">{row.dailyMessages.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STEP 3 — Messages */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="text-sm text-white/80">
              Share two sample text messages to represent the messages you plan to send.
            </div>
            <ComplianceWarning>
              We strongly recommend not editing the text messages below to avoid delays in A2P 10DLC registration.
            </ComplianceWarning>
            <div>
              <label className="label" htmlFor="sample-1">Sample message 1</label>
              <textarea id="sample-1" rows={3} className="input mt-1 font-mono text-xs"
                value={sample1} onChange={(e) => setSample1(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="sample-2">Sample message 2</label>
              <textarea id="sample-2" rows={3} className="input mt-1 font-mono text-xs"
                value={sample2} onChange={(e) => setSample2(e.target.value)} />
            </div>
            <button type="button" onClick={resetMessages} className="text-xs text-civix-purple hover:underline inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reset to recommended
            </button>
          </div>
        )}

        {/* STEP 4 — CTA */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="text-sm text-white/80">
              Describe your call-to-action — how customers opt in to receive text messages from you.
            </div>
            <ComplianceWarning>
              We strongly recommend not editing the CTA description below to help you stay compliant and avoid delays.
            </ComplianceWarning>
            <textarea rows={6} className="input font-mono text-xs"
              value={cta} onChange={(e) => setCta(e.target.value)} />
            <button type="button" onClick={resetCta} className="text-xs text-civix-purple hover:underline inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reset to recommended
            </button>
          </div>
        )}

        {/* STEP 5 — Review */}
        {step === 4 && (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-amber-400/50 bg-amber-400/10 p-3 text-xs text-amber-100">
              <strong>IMPORTANT — REVIEW BEFORE PROCEEDING.</strong>
              {' '}When you submit, the information you entered will be sent to The Campaign Registry.
              Make sure your use case description and sample messages are compliant.
            </div>
            <ReviewRow label="Use case" value={useCase} />
            <ReviewRow label="Description" value={useCaseDescription} mono />
            <ReviewRow label="Sample message 1" value={sample1} mono />
            <ReviewRow label="Sample message 2" value={sample2} mono />
            <ReviewRow label="CTA" value={cta} mono />

            <div className="rounded-md border border-civix-purple/40 bg-civix-purple/10 p-3 text-xs space-y-1">
              <div><strong>Today: $25 one-time</strong> — $15 TCR activation + first month Twilio campaign fee</div>
              <div><strong>Starting month 2: $10/mo recurring</strong> — Twilio campaign fee, charged at cost</div>
              <div className="italic text-white/55 pt-1">CIVIX charges these at cost. We don't profit from A2P registration.</div>
            </div>

            <label className="flex items-start gap-2 text-xs text-white/80">
              <input type="checkbox" className="accent-civix-purple mt-0.5"
                checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>
                As the owner of this business profile, I agree to be charged
                {' '}<strong>$25 today</strong> and <strong>$10/month</strong> thereafter while this A2P campaign is active.
                The campaign auto-cancels if my CIVIX subscription becomes inactive.
              </span>
            </label>

            {serverError && (
              <div role="alert" className="rounded-md border border-rose-500/50 bg-rose-500/10 p-3 text-xs text-rose-200">
                <strong>{serverError.code === 'card_declined' ? 'Card declined' : 'Submission failed'}:</strong> {serverError.msg}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-3 border-t border-civix-border/40">
          <button onClick={back} disabled={step === 0 || submitting}
            className="btn-ghost inline-flex items-center gap-1 disabled:opacity-30">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="btn-primary inline-flex items-center gap-2">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!agreed || submitting}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Submitting…' : 'SUBMIT FOR REVIEW'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="font-mono text-white/85">{value || '—'}</div>
    </div>
  );
}

function ComplianceWarning({ children }) {
  return (
    <div className="rounded-md border border-amber-400/50 bg-amber-400/10 p-3 text-xs text-amber-100 inline-flex items-start gap-2">
      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
      <div><strong>Ensure compliance to avoid delays —</strong> {children}</div>
    </div>
  );
}

function ReviewRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`text-white/80 ${mono ? 'font-mono text-xs whitespace-pre-wrap' : ''}`}>{value || '—'}</div>
    </div>
  );
}
